import { type ToolSpec, n, s, fmt } from "./types";

const WD = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const addMin = (hhmm: string, minutes: number) => {
  const [h, m] = hhmm.split(":").map(Number);
  const t = ((h * 60 + m + minutes) % 1440 + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export const DATETIME_TOOLS: ToolSpec[] = [
  {
    slug: "chronological-date-gap-calculator",
    cluster: "datetime",
    name: "Chronological Date Gap",
    h1: "Chronological Age Calculator — Exact Date Difference in Days",
    title: "Exact Date Difference Calculator (Years, Months, Days)",
    description:
      "Calculate the exact gap between two dates in years, months, days, total weeks, hours and minutes, with leap-year handling and business-day context.",
    keywords: [
      "chronological age calculator exact date difference days",
      "how many days between two dates including leap years",
      "date difference in years months and days",
      "exact age as on a specific date for forms",
    ],
    inputs: [
      { id: "start", label: "Start date / date of birth", type: "date", def: "1995-06-15" },
      { id: "end", label: "End date", type: "date", def: "2026-08-06" },
    ],
    compute: (v) => {
      const a = new Date(s(v, "start")), b = new Date(s(v, "end"));
      const valid = !isNaN(a.getTime()) && !isNaN(b.getTime());
      const ms = valid ? Math.abs(b.getTime() - a.getTime()) : 0;
      const days = Math.floor(ms / 86400000);
      let y = b.getFullYear() - a.getFullYear();
      let mo = b.getMonth() - a.getMonth();
      let d = b.getDate() - a.getDate();
      if (d < 0) { mo--; d += new Date(b.getFullYear(), b.getMonth(), 0).getDate(); }
      if (mo < 0) { y--; mo += 12; }
      const weekdays = Math.floor(days / 7) * 5 + Math.min(5, days % 7);
      return {
        cards: [
          { label: "Exact difference", value: `${y}y ${mo}m ${d}d`, sub: "Calendar-accurate", accent: true },
          { label: "Total days", value: fmt(days), sub: `${fmt(Math.floor(days / 7))} weeks` },
          { label: "Total hours", value: fmt(days * 24), sub: `${fmt(days * 1440)} minutes` },
          { label: "Approx. weekdays", value: fmt(weekdays), sub: "Excluding Saturdays and Sundays" },
        ],
        table: {
          title: "The same gap in every unit",
          head: ["Unit", "Value"],
          rows: [
            ["Years (decimal)", fmt(days / 365.2425, 2)],
            ["Months (average)", fmt(days / 30.4369, 1)],
            ["Weeks", fmt(days / 7, 1)],
            ["Days", fmt(days)],
            ["Hours", fmt(days * 24)],
            ["Minutes", fmt(days * 1440)],
            ["Seconds", fmt(days * 86400)],
          ],
        },
        summary: valid ? `Start date fell on a ${WD[a.getDay()]}; the end date is a ${WD[b.getDay()]}.` : "Enter two valid dates.",
      };
    },
    intro:
      "Date arithmetic looks trivial until leap years, uneven month lengths and daylight saving transitions get involved. The chronological method used by governments, exam boards and HR systems subtracts whole years first, then whole months, then borrows days from the preceding calendar month — which is why 30 January to 1 March is one month and one day, not thirty days.",
    sections: [
      { h: "Why total days and years-months-days disagree", body: "A gap of exactly 365 days is one year only in a non-leap span. Because the Gregorian year averages 365.2425 days, converting a large day count into a decimal year and reading off the whole part will drift from the calendar answer by up to a day per four years. Always use the calendar method for legal age." },
      { h: "Leap year rules in full", body: "A year is a leap year if it is divisible by four, except centuries, unless the century is divisible by four hundred. 1900 was not a leap year; 2000 was. Any date span crossing 29 February gains a day that naive month-multiplication misses." },
      { h: "'Age as on' cut-off dates", body: "Competitive exams and school admissions rarely use today's date. They fix an 'age as on' date such as 1 January or 31 August. Set the end date field to that cut-off and the tool returns the figure the application form expects, not your age today." },
    ],
    staticTable: {
      title: "Common date-gap reference points",
      head: ["Span", "Total days", "Weeks", "Contains leap days"],
      rows: [
        ["1 year (common)", "365", "52.14", "No"],
        ["1 year (leap)", "366", "52.29", "Yes"],
        ["4 years", "1,461", "208.71", "One"],
        ["10 years", "3,652", "521.71", "Two or three"],
        ["18 years", "6,575", "939.29", "Four or five"],
        ["100 years", "36,524", "5,217.71", "24 or 25"],
      ],
    },
    faqs: [
      { q: "How do I calculate the exact number of days between two dates?", a: "Convert both dates to a day count from a fixed epoch and subtract. This automatically accounts for leap years and differing month lengths, which is what the tool above does internally." },
      { q: "Why does my age differ by a day from another calculator?", a: "Usually a time zone or inclusive-counting difference. Some tools count both endpoints; the standard chronological method counts the interval, not the endpoints." },
      { q: "Is age calculated the same way everywhere?", a: "Not quite. East Asian age reckoning traditionally counts a person as one at birth. International legal and medical use follows the Western chronological method applied here." },
    ],
    related: ["business-days-calculator", "day-of-week-born-calculator", "milestone-birthday-countdown", "half-plus-seven-age-gap-calculator"],
  },

  {
    slug: "half-plus-seven-age-gap-calculator",
    cluster: "datetime",
    name: "Half-Plus-Seven Relationship Rule",
    h1: "Half Plus Seven Age Gap Calculator (Standard Creepiness Dating Rule)",
    title: "Half Plus Seven Rule Calculator — Age Gap Check",
    description:
      "Check any age gap against the half-your-age-plus-seven rule, see the socially acceptable dating range for both partners and the percentage age difference.",
    keywords: [
      "half plus seven age gap calculator dating rule",
      "socially acceptable age range formula",
      "is a 10 year age gap too much calculator",
      "youngest age you can date rule",
    ],
    inputs: [
      { id: "you", label: "Your age", type: "number", def: "32", unit: "years", min: 14, max: 100 },
      { id: "partner", label: "Partner's age", type: "number", def: "26", unit: "years", min: 14, max: 100 },
    ],
    compute: (v) => {
      const a = n(v, "you"), b = n(v, "partner");
      const minFor = (x: number) => x / 2 + 7;
      const maxFor = (x: number) => (x - 7) * 2;
      const ok = b >= minFor(a) && a >= minFor(b);
      const gap = Math.abs(a - b);
      return {
        cards: [
          { label: "Rule verdict", value: ok ? "Within the rule" : "Outside the rule", sub: `Gap of ${gap} year${gap === 1 ? "" : "s"}`, accent: true },
          { label: "Your acceptable range", value: `${fmt(minFor(a), 1)} – ${fmt(maxFor(a), 1)}`, sub: `At age ${a}` },
          { label: "Their acceptable range", value: `${fmt(minFor(b), 1)} – ${fmt(maxFor(b), 1)}`, sub: `At age ${b}` },
          { label: "Relative difference", value: `${fmt((gap / Math.max(a, b)) * 100, 1)}%`, sub: "Gap as a share of the older age" },
        ],
        table: {
          title: "How the window widens with age",
          head: ["Age", "Youngest by the rule", "Oldest by the rule", "Window width"],
          rows: [20, 25, 30, 35, 40, 50, 60].map((x) => [x, fmt(minFor(x), 1), fmt(maxFor(x), 1), `${fmt(maxFor(x) - minFor(x), 1)} yrs`]),
        },
        summary: `At ${a} the rule allows partners from ${fmt(minFor(a), 1)} upward; at ${b} it allows from ${fmt(minFor(b), 1)} upward.`,
      };
    },
    intro:
      "The half-your-age-plus-seven guideline appeared in nineteenth-century etiquette writing and resurfaced through pop culture as a rough social heuristic. It is not law, science or advice — but it is a neat piece of arithmetic, because the acceptable window widens as people age: five years wide at twenty, twenty-six years wide at fifty.",
    sections: [
      { h: "How the formula actually behaves", body: "The lower bound is age ÷ 2 + 7 and the upper bound is its inverse, (age − 7) × 2. Both are linear, but the upper bound has twice the slope, so the permitted range expands at one extra year of width for every year you age. Below 14 the formula returns a lower bound above the person's own age, which is why it is meaningless for minors." },
      { h: "What research says about age gaps", body: "Demographic surveys consistently find that the average heterosexual couple has a gap of two to three years with the man older. Larger gaps correlate with slightly higher separation rates in some longitudinal studies, but the effect sizes are small compared with financial stress, communication quality and shared values." },
      { h: "Where the rule breaks down", body: "The heuristic ignores life stage entirely. A 26 and 33 pairing where one is finishing a doctorate and the other has three children faces more friction than a 40 and 52 pairing at similar stages. Treat the number as a conversation starter, not a compatibility test." },
    ],
    staticTable: {
      title: "The rule at a glance",
      head: ["Your age", "Minimum partner age", "Maximum partner age", "Gap allowed downward"],
      rows: [
        [18, "16.0", "22.0", "2.0 yrs"],
        [22, "18.0", "30.0", "4.0 yrs"],
        [28, "21.0", "42.0", "7.0 yrs"],
        [34, "24.0", "54.0", "10.0 yrs"],
        [45, "29.5", "76.0", "15.5 yrs"],
        [60, "37.0", "106.0", "23.0 yrs"],
      ],
    },
    faqs: [
      { q: "What is the half your age plus seven rule?", a: "It suggests the youngest socially acceptable partner age is half your age plus seven. At 32 that is 23; at 50 it is 32. The upper bound is the inverse: (your age − 7) × 2." },
      { q: "Is a ten year age gap acceptable?", a: "By this rule it becomes permissible once the older partner is around 34. Socially, gaps matter less as both people age and life stages converge." },
      { q: "Does the rule apply to teenagers?", a: "No. Below about eighteen the formula produces nonsensical results, and legal age-of-consent laws override any heuristic." },
    ],
    related: ["chronological-date-gap-calculator", "milestone-birthday-countdown", "day-of-week-born-calculator", "dog-age-by-breed-calculator"],
  },

  {
    slug: "sleep-cycle-rem-calculator",
    cluster: "datetime",
    name: "REM Sleep Cycle Scheduler",
    h1: "REM Sleep Cycle Calculator (Optimal Bedtime and Wake-Up Scheduler)",
    title: "REM Sleep Cycle Calculator — Bedtime & Wake Times",
    description:
      "Find bedtimes and wake times that land at the end of a 90-minute sleep cycle, including fall-asleep latency, so you wake in light sleep instead of deep sleep.",
    keywords: [
      "sleep cycle calculator rem sleep bedtime wake up",
      "best time to go to sleep if i wake up at 6am",
      "90 minute sleep cycle calculator",
      "how to avoid sleep inertia wake up refreshed",
      "sleep cycle formula for 7.5 hours sleep",
    ],
    inputs: [
      { id: "mode", label: "Mode", type: "select", def: "wake", options: [
        { value: "wake", label: "I want to wake up at…" },
        { value: "bed", label: "If I go to bed at…" },
      ] },
      { id: "time", label: "Selected time", type: "time", def: "06:30" },
      { id: "latency", label: "Time to fall asleep", type: "number", def: "14", unit: "minutes", min: 0, max: 90 },
      { id: "cycle", label: "Cycle length", type: "number", def: "90", unit: "minutes", min: 70, max: 110, step: 5 },
    ],
    compute: (v) => {
      const mode = s(v, "mode", "wake"), t = s(v, "time", "06:30");
      const lat = n(v, "latency", 14), cyc = n(v, "cycle", 90);
      const rows: (string | number)[][] = [];
      const cards = [];
      for (const c of [6, 5, 4, 3]) {
        const offset = c * cyc + lat;
        const clock = mode === "wake" ? addMin(t, -offset) : addMin(t, offset);
        const hrs = (c * cyc) / 60;
        const quality = c >= 5 ? "Ideal" : c === 4 ? "Acceptable" : "Short — nap territory";
        rows.push([`${c} cycles`, `${hrs} h sleep`, clock, quality]);
        if (c === 6 || c === 5) cards.push({ label: `${c} cycles (${hrs} h)`, value: clock, sub: mode === "wake" ? "Go to bed at this time" : "Wake up at this time", accent: c === 5 });
      }
      cards.push({ label: "Fall-asleep buffer", value: `${lat} min`, sub: "Added to every calculation" });
      cards.push({ label: "Cycle length", value: `${cyc} min`, sub: "Adults average 90-110 minutes" });
      return {
        cards,
        table: { title: mode === "wake" ? "Bedtimes that end on a full cycle" : "Wake times that end on a full cycle", head: ["Cycles", "Total sleep", "Clock time", "Sleep inertia risk"], rows },
        summary: "Waking at the end of a cycle means surfacing from light N1/N2 sleep or REM rather than from slow-wave sleep.",
      };
    },
    intro:
      "Sleep is not uniform. It runs in cycles of roughly ninety minutes, each descending through light N1 and N2 into deep N3 slow-wave sleep before climbing back into REM. Waking at the top of that arc feels effortless; waking from the bottom of N3 produces sleep inertia — the groggy, disoriented state that can last twenty to thirty minutes regardless of total sleep duration.",
    sections: [
      { h: "The four stages in one cycle", body: "N1 is the two to five minute drift-off phase. N2 occupies roughly half of total sleep and features sleep spindles tied to memory consolidation. N3 is slow-wave sleep, concentrated in the first half of the night, when growth hormone peaks and physical restoration happens. REM lengthens with each successive cycle and dominates the final hours, which is why cutting sleep short costs REM disproportionately." },
      { h: "Why the calculator adds latency", body: "Sleep onset takes most healthy adults ten to twenty minutes. If you plan to be in bed at 22:30 for a 06:00 alarm, you are planning 7.5 hours in bed but only about 7.2 hours asleep — enough to land the alarm mid-cycle. Adding latency shifts every recommendation to the time you should lie down, not the time you expect to be unconscious." },
      { h: "Ninety minutes is an average, not a constant", body: "Individual cycles range from about seventy to one hundred and ten minutes and lengthen slightly through the night. The cycle-length field lets you tune it: if you consistently wake groggy on the ninety-minute schedule, try ninety-five or one hundred for a week and compare." },
    ],
    staticTable: {
      title: "Sleep architecture across a typical night",
      head: ["Cycle", "Approx. time", "N3 deep sleep", "REM", "Wake difficulty"],
      rows: [
        ["1st", "0:00-1:30", "35-40 min", "5-10 min", "Very hard"],
        ["2nd", "1:30-3:00", "25-30 min", "15-20 min", "Hard"],
        ["3rd", "3:00-4:30", "10-15 min", "25-30 min", "Moderate"],
        ["4th", "4:30-6:00", "5 min", "35-40 min", "Easy"],
        ["5th", "6:00-7:30", "0-5 min", "40-50 min", "Easiest"],
      ],
    },
    faqs: [
      { q: "Why does waking in the middle of a sleep cycle make me feel tired?", a: "Waking during stage 3 slow-wave sleep triggers sleep inertia, because brain wave activity is at its slowest. Waking at the end of a ninety-minute cycle means surfacing from light sleep or REM, which feels far easier." },
      { q: "What time should I go to bed to wake up at 6am?", a: "For five full cycles plus fourteen minutes of fall-asleep time, around 22:16. For six cycles, around 20:46. The calculator prints both for your own latency." },
      { q: "Is 7.5 hours enough sleep?", a: "Five full cycles suits many adults, but the recommended range remains seven to nine hours. If you wake unrefreshed on 7.5 hours consistently, try six cycles for a fortnight." },
    ],
    related: ["caffeine-half-life-calculator", "eye-strain-20-20-20-timer", "milestone-birthday-countdown", "target-heart-rate-zone-calculator"],
  },

  {
    slug: "milestone-birthday-countdown",
    cluster: "datetime",
    name: "Milestone Birthday Countdown",
    h1: "Milestone Birthday Countdown — Days to Your 18th, 21st, 30th and 50th",
    title: "Milestone Birthday Countdown Calculator",
    description:
      "Count down the exact days, weeks and hours to every milestone birthday from your date of birth, plus the weekday each milestone falls on.",
    keywords: [
      "milestone birthday countdown days to 18th 21st 30th 50th",
      "how many days until my 30th birthday",
      "what day of the week will my 40th birthday be",
      "birthday countdown calculator by date of birth",
    ],
    inputs: [
      { id: "dob", label: "Date of birth", type: "date", def: "1998-03-12" },
    ],
    compute: (v) => {
      const dob = new Date(s(v, "dob"));
      const now = new Date();
      const valid = !isNaN(dob.getTime());
      const ms = valid ? now.getTime() - dob.getTime() : 0;
      const ageYears = ms / (365.2425 * 86400000);
      const milestones = [16, 18, 21, 25, 30, 40, 50, 60, 65, 70, 80, 100];
      const rows: (string | number)[][] = milestones.map((m) => {
        const d = new Date(dob); d.setFullYear(dob.getFullYear() + m);
        const days = Math.ceil((d.getTime() - now.getTime()) / 86400000);
        return [`${m}th`, d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }), WD[d.getDay()], days > 0 ? `${fmt(days)} days away` : `${fmt(-days)} days ago`];
      });
      const next = milestones.find((m) => m > ageYears) ?? 100;
      const nd = new Date(dob); nd.setFullYear(dob.getFullYear() + next);
      const dLeft = Math.max(0, Math.ceil((nd.getTime() - now.getTime()) / 86400000));
      const nextBd = new Date(now.getFullYear(), dob.getMonth(), dob.getDate());
      if (nextBd < now) nextBd.setFullYear(now.getFullYear() + 1);
      return {
        cards: [
          { label: `Next milestone: ${next}`, value: `${fmt(dLeft)} days`, sub: `${fmt(dLeft / 7, 1)} weeks · ${fmt(dLeft / 30.44, 1)} months`, accent: true },
          { label: "Current age", value: `${Math.floor(ageYears)} years`, sub: `${fmt(ms / 86400000)} days lived` },
          { label: "Next birthday", value: nextBd.toLocaleDateString("en-GB", { day: "numeric", month: "short" }), sub: `Falls on a ${WD[nextBd.getDay()]}` },
          { label: "Milestone weekday", value: WD[nd.getDay()], sub: `Your ${next}th lands here` },
        ],
        table: { title: "Every milestone birthday", head: ["Milestone", "Date", "Weekday", "Countdown"], rows },
      };
    },
    intro:
      "Milestone birthdays carry legal and cultural weight that ordinary ones do not: driving at sixteen or seventeen, majority at eighteen, drinking age at twenty-one in the United States, pension access in the sixties. This countdown resolves each of them to an exact date and weekday from your date of birth, which is what party planning and paperwork actually need.",
    sections: [
      { h: "Why the weekday matters", body: "Because 365 is not divisible by seven, your birthday advances one weekday each year and two after a leap day. That produces the familiar twenty-eight-year cycle in which the same date returns to the same weekday. Knowing that a fortieth lands on a Wednesday changes whether you plan for the day itself or the weekend around it." },
      { h: "Legal milestones by country", body: "Majority is eighteen across most of the world, sixteen in Scotland for many purposes and twenty-one in a handful of jurisdictions. US alcohol purchase is twenty-one; UK and most of Europe is eighteen. Car hire commonly carries surcharges under twenty-five. Retirement access ranges from fifty-five for private pensions to sixty-seven for state pensions." },
      { h: "Leap-day birthdays", body: "If you were born on 29 February, most legal systems treat 1 March as the anniversary in common years, while some use 28 February. The countdown here rolls to the nearest valid date, so the day count stays accurate either way." },
    ],
    staticTable: {
      title: "What each milestone unlocks",
      head: ["Age", "United States", "United Kingdom", "Australia / Canada"],
      rows: [
        [16, "Learner permit", "Part-time work, moped", "Learner permit"],
        [18, "Vote, contracts, military", "Vote, alcohol, full licence", "Vote, alcohol (AU)"],
        [21, "Alcohol, handgun purchase", "—", "Alcohol (some CA provinces at 19)"],
        [25, "Car hire without surcharge", "Car hire without surcharge", "Car hire without surcharge"],
        [50, "Catch-up retirement contributions", "—", "—"],
        [59.5, "Penalty-free 401(k) access", "—", "—"],
        [65, "Medicare eligibility", "—", "Age pension (varies)"],
      ],
    },
    faqs: [
      { q: "How many days until my 30th birthday?", a: "Enter your date of birth above — the calculator prints the exact day count, week count and the weekday your thirtieth falls on." },
      { q: "What weekday will my birthday be in a future year?", a: "Each year the weekday advances by one, or two across a leap day. The milestone table above resolves each future date directly rather than relying on that shortcut." },
      { q: "How are leap-day birthdays counted?", a: "Most legal frameworks treat 1 March as the anniversary in non-leap years. The tool rolls to the nearest valid calendar date so countdowns remain exact." },
    ],
    related: ["chronological-date-gap-calculator", "day-of-week-born-calculator", "business-days-calculator", "half-plus-seven-age-gap-calculator"],
  },

  {
    slug: "dog-age-by-breed-calculator",
    cluster: "datetime",
    name: "Dog Age by Breed Size",
    h1: "Dog Age in Human Years Calculator (By Breed Size Category)",
    title: "Dog Age in Human Years Calculator by Breed Size",
    description:
      "Convert your dog's age to human years using size-adjusted veterinary tables rather than the outdated seven-year rule, plus life-stage and care guidance.",
    keywords: [
      "dog age in human years calculator by breed size",
      "is one dog year really seven human years",
      "large breed dog aging chart",
      "senior dog age by weight category",
    ],
    inputs: [
      { id: "age", label: "Dog's age", type: "number", def: "5", unit: "years", min: 0.1, max: 25, step: 0.5 },
      { id: "size", label: "Breed size", type: "select", def: "medium", options: [
        { value: "small", label: "Small — under 9 kg (Chihuahua, Dachshund)" },
        { value: "medium", label: "Medium — 9-23 kg (Beagle, Border Collie)" },
        { value: "large", label: "Large — 23-40 kg (Labrador, German Shepherd)" },
        { value: "giant", label: "Giant — over 40 kg (Great Dane, Mastiff)" },
      ] },
    ],
    compute: (v) => {
      const a = n(v, "age", 1), size = s(v, "size", "medium");
      const perYear = { small: 4, medium: 5, large: 5.5, giant: 7.5 }[size] ?? 5;
      const human = a <= 1 ? a * 15 : a <= 2 ? 15 + (a - 1) * 9 : 24 + (a - 2) * perYear;
      const expectancy = { small: 15, medium: 13, large: 11, giant: 9 }[size] ?? 13;
      const stage = a < 0.5 ? "Puppy" : a < 1 ? "Junior" : a < 3 ? "Young adult" : a < expectancy * 0.6 ? "Adult" : a < expectancy * 0.85 ? "Mature" : "Senior";
      return {
        cards: [
          { label: "Human age equivalent", value: `${fmt(human)} years`, sub: `${size} breed scale`, accent: true },
          { label: "Life stage", value: stage, sub: `${fmt((a / expectancy) * 100)}% of typical lifespan` },
          { label: "Typical life expectancy", value: `${expectancy} years`, sub: "Breed-size average" },
          { label: "Years remaining (avg)", value: `${fmt(Math.max(0, expectancy - a), 1)}`, sub: "Median, not a prediction" },
        ],
        table: {
          title: "Your dog's ageing curve",
          head: ["Dog age", "Human equivalent", "Life stage"],
          rows: [0.5, 1, 2, 3, 5, 7, 10, 13, 16].map((x) => {
            const h = x <= 1 ? x * 15 : x <= 2 ? 15 + (x - 1) * 9 : 24 + (x - 2) * perYear;
            const st = x < 1 ? "Puppy" : x < 3 ? "Young adult" : x < expectancy * 0.6 ? "Adult" : x < expectancy * 0.85 ? "Mature" : "Senior";
            return [`${x} yr`, `${fmt(h)} yrs`, st];
          }),
        },
        summary: "The first year alone is worth about fifteen human years — puppy development is far faster than the linear rule suggests.",
      };
    },
    intro:
      "The seven-year rule is folklore. Dogs reach sexual maturity within their first year, which no seven-year multiplier can represent, and then age at rates that diverge sharply by body size: a Great Dane is geriatric at seven while a Chihuahua is barely middle-aged. This calculator uses the size-stratified veterinary curve instead.",
    sections: [
      { h: "Why big dogs age faster", body: "Large breeds grow to sixty times their birth weight in a year, and that accelerated cell division correlates with earlier onset of osteosarcoma, cardiomyopathy and joint degeneration. Each additional ten kilograms of adult body mass costs roughly one month of median life expectancy across large cohort studies." },
      { h: "The first two years are non-linear", body: "Year one maps to roughly fifteen human years across all sizes, year two adds about nine more, and only from year three does the size-specific annual multiplier apply. That is why a two-year-old dog of any breed behaves like a young adult rather than a child." },
      { h: "What changes at senior stage", body: "Once a dog passes about seventy percent of its breed life expectancy, twice-yearly veterinary checks, bloodwork, joint support and a switch to a senior diet become genuinely useful. Cognitive dysfunction, reduced hearing and slower recovery from exercise are the usual first signs." },
    ],
    staticTable: {
      title: "Dog age to human years by size category",
      head: ["Dog age", "Small (<9 kg)", "Medium (9-23 kg)", "Large (23-40 kg)", "Giant (>40 kg)"],
      rows: [
        ["1 yr", "15", "15", "15", "15"],
        ["2 yr", "24", "24", "24", "24"],
        ["5 yr", "36", "39", "41", "47"],
        ["8 yr", "48", "54", "57", "69"],
        ["10 yr", "56", "64", "68", "84"],
        ["13 yr", "68", "79", "85", "107"],
        ["16 yr", "80", "94", "101", "—"],
      ],
    },
    faqs: [
      { q: "Is one dog year really seven human years?", a: "No. The first year equates to about fifteen human years and the second to about nine more. After that, small breeds age roughly four human years annually while giant breeds age about seven and a half." },
      { q: "At what age is a dog considered senior?", a: "Roughly seven for giant breeds, eight to nine for large, ten for medium and eleven to twelve for small breeds — about seventy percent of breed life expectancy." },
      { q: "Which dog breeds live longest?", a: "Small terriers, Chihuahuas, toy poodles and dachshunds routinely reach fifteen to eighteen years. Great Danes, Bernese mountain dogs and mastiffs average seven to nine." },
    ],
    related: ["cat-age-to-human-years-converter", "chronological-date-gap-calculator", "milestone-birthday-countdown", "houseplant-watering-schedule-calculator"],
  },

  {
    slug: "cat-age-to-human-years-converter",
    cluster: "datetime",
    name: "Cat Age to Human Years",
    h1: "Cat Age to Human Years Converter (Exact Feline Life Stages)",
    title: "Cat Age to Human Years Converter — Feline Life Stages",
    description:
      "Convert cat age in months or years to human equivalent using AAFP feline life stages, with indoor versus outdoor life expectancy and senior care markers.",
    keywords: [
      "cat age to human years converter exact feline stages",
      "how old is my cat in human years chart",
      "kitten age in months to human years",
      "when is a cat considered senior",
    ],
    inputs: [
      { id: "unit", label: "Age given in", type: "select", def: "years", options: [{ value: "years", label: "Years" }, { value: "months", label: "Months" }] },
      { id: "age", label: "Cat's age", type: "number", def: "7", min: 0.1, max: 300, step: 0.5 },
      { id: "living", label: "Living situation", type: "select", def: "indoor", options: [
        { value: "indoor", label: "Indoor only" },
        { value: "mixed", label: "Indoor with garden access" },
        { value: "outdoor", label: "Outdoor / farm cat" },
      ] },
    ],
    compute: (v) => {
      const yrs = s(v, "unit") === "months" ? n(v, "age") / 12 : n(v, "age");
      const human = yrs <= 1 ? yrs * 15 : yrs <= 2 ? 15 + (yrs - 1) * 9 : 24 + (yrs - 2) * 4;
      const expectancy = { indoor: 16, mixed: 13, outdoor: 8 }[s(v, "living", "indoor")] ?? 15;
      const stage = yrs < 0.5 ? "Kitten" : yrs < 2 ? "Junior" : yrs < 7 ? "Prime adult" : yrs < 11 ? "Mature" : yrs < 15 ? "Senior" : "Geriatric";
      return {
        cards: [
          { label: "Human age equivalent", value: `${fmt(human)} years`, sub: stage, accent: true },
          { label: "AAFP life stage", value: stage, sub: `${fmt((yrs / expectancy) * 100)}% of expected lifespan` },
          { label: "Life expectancy", value: `${expectancy} years`, sub: `${s(v, "living", "indoor")} lifestyle` },
          { label: "Cat age", value: `${fmt(yrs, 1)} yrs`, sub: `${fmt(yrs * 12)} months` },
        ],
        table: {
          title: "Feline ageing curve",
          head: ["Cat age", "Human equivalent", "AAFP stage", "Care focus"],
          rows: [
            ["2 months", "2-3 yrs", "Kitten", "Vaccination, socialisation"],
            ["6 months", "10 yrs", "Kitten", "Neutering window"],
            ["1 year", "15 yrs", "Junior", "Adult diet transition"],
            ["2 years", "24 yrs", "Junior", "Dental baseline"],
            ["5 years", "36 yrs", "Prime", "Weight management"],
            ["8 years", "48 yrs", "Mature", "Annual bloodwork"],
            ["11 years", "60 yrs", "Senior", "Kidney and thyroid screening"],
            ["15 years", "76 yrs", "Geriatric", "Six-monthly checks"],
            ["20 years", "96 yrs", "Geriatric", "Palliative comfort care"],
          ],
        },
      };
    },
    intro:
      "Cats compress an entire childhood into their first year: a six-month kitten is roughly a ten-year-old child, and a one-year-old cat is a fifteen-year-old teenager. After the second birthday the curve flattens to about four human years annually, which is gentler than the canine equivalent and is why indoor cats commonly reach the human equivalent of ninety.",
    sections: [
      { h: "The AAFP life-stage framework", body: "The American Association of Feline Practitioners defines kitten (0-1), young adult (1-6), mature (7-10), senior (11-14) and geriatric (15+). Veterinary protocols change at each boundary: annual bloodwork from mature, kidney and thyroid screening from senior, and six-monthly examinations from geriatric." },
      { h: "Indoor versus outdoor lifespan", body: "Indoor-only cats average fifteen to seventeen years. Cats with unsupervised outdoor access average closer to twelve, and true outdoor or farm cats often under nine, driven overwhelmingly by road traffic, territorial fights and infectious disease rather than by ageing itself." },
      { h: "Early signals of feline ageing", body: "Weight loss with a normal or increased appetite is the classic hyperthyroid signal in mature cats. Increased thirst and urination point toward chronic kidney disease. Both are common, both are manageable when caught early, and both are the reason senior screening exists." },
    ],
    staticTable: {
      title: "Cat years to human years reference",
      head: ["Cat age", "Human years", "Stage", "Typical vet visit frequency"],
      rows: [
        ["3 months", "4", "Kitten", "Every 3-4 weeks"],
        ["1 year", "15", "Junior", "Annual"],
        ["3 years", "28", "Prime adult", "Annual"],
        ["7 years", "44", "Mature", "Annual + bloodwork"],
        ["10 years", "56", "Mature", "Annual + bloodwork"],
        ["14 years", "72", "Senior", "Every 6 months"],
        ["18 years", "88", "Geriatric", "Every 6 months"],
        ["22 years", "104", "Geriatric", "Every 3-6 months"],
      ],
    },
    faqs: [
      { q: "How old is a 7 year old cat in human years?", a: "About forty-four human years — a mature adult. Cats gain roughly four human-equivalent years annually after their second birthday." },
      { q: "When is a cat considered senior?", a: "From eleven years under the AAFP framework, which corresponds to about sixty human years. Screening for kidney disease and hyperthyroidism starts here." },
      { q: "Do indoor cats live longer than outdoor cats?", a: "Substantially. Indoor cats average fifteen to seventeen years against roughly eight to twelve for cats with free outdoor access, mostly due to traffic and infectious disease risk." },
    ],
    related: ["dog-age-by-breed-calculator", "chronological-date-gap-calculator", "houseplant-watering-schedule-calculator", "milestone-birthday-countdown"],
  },

  {
    slug: "timezone-meeting-overlap-finder",
    cluster: "datetime",
    name: "Remote Team Slot Overlap",
    h1: "Remote Team Time Zone Overlap and Meeting Slot Finder",
    title: "Time Zone Overlap Meeting Slot Finder for Remote Teams",
    description:
      "Find working-hour overlap between up to three time zones and get the meeting slots that are civil for everyone, with a full hour-by-hour comparison grid.",
    keywords: [
      "remote team time zone overlap meeting slot finder",
      "best meeting time between london and new york",
      "working hours overlap calculator three time zones",
      "async handover window calculator distributed team",
    ],
    inputs: [
      { id: "base", label: "Your UTC offset", type: "number", def: "0", unit: "hours", min: -12, max: 14, step: 0.5 },
      { id: "b", label: "Colleague B UTC offset", type: "number", def: "-5", unit: "hours", min: -12, max: 14, step: 0.5 },
      { id: "c", label: "Colleague C UTC offset (optional)", type: "number", def: "5.5", unit: "hours", min: -12, max: 14, step: 0.5 },
      { id: "start", label: "Working day starts", type: "number", def: "9", unit: ":00", min: 0, max: 23 },
      { id: "end", label: "Working day ends", type: "number", def: "17", unit: ":00", min: 1, max: 24 },
    ],
    compute: (v) => {
      const offs = [n(v, "base"), n(v, "b"), n(v, "c")];
      const st = n(v, "start", 9), en = n(v, "end", 17);
      const rows: (string | number)[][] = [];
      let overlapHours = 0;
      let firstSlot = "";
      for (let utc = 0; utc < 24; utc++) {
        const locals = offs.map((o) => ((utc + o) % 24 + 24) % 24);
        const ok = locals.every((l) => l >= st && l < en);
        if (ok) { overlapHours++; if (!firstSlot) firstSlot = `${String(utc).padStart(2, "0")}:00 UTC`; }
        rows.push([
          `${String(utc).padStart(2, "0")}:00 UTC`,
          `${fmt(locals[0], locals[0] % 1 ? 1 : 0)}:00`,
          `${fmt(locals[1], locals[1] % 1 ? 1 : 0)}:00`,
          `${fmt(locals[2], locals[2] % 1 ? 1 : 0)}:00`,
          ok ? "✅ All available" : "—",
        ]);
      }
      return {
        cards: [
          { label: "Overlap window", value: `${overlapHours} h`, sub: overlapHours ? "Everyone inside working hours" : "No shared working hours", accent: true },
          { label: "First shared slot", value: firstSlot || "None", sub: "Earliest civil meeting time" },
          { label: "Widest pair gap", value: `${fmt(Math.max(...offs) - Math.min(...offs), 1)} h`, sub: "Between the extreme zones" },
          { label: "Async handover", value: overlapHours < 2 ? "Required" : "Optional", sub: overlapHours < 2 ? "Document instead of meeting" : "Live sync is viable" },
        ],
        table: { title: "Hour-by-hour availability grid", head: ["UTC", "You", "Colleague B", "Colleague C", "Status"], rows },
        summary: overlapHours >= 3
          ? "A three-hour or wider overlap supports a daily stand-up plus one deep-work sync."
          : "With a narrow overlap, protect the window for decisions only and move status updates to writing.",
      };
    },
    intro:
      "Distributed teams do not fail because of time zones; they fail because nobody measured the overlap before designing the working agreement. Two zones eight hours apart still share about one civil hour a day. Three zones spanning thirteen hours often share none — which is a scheduling constraint, not a scheduling problem, once you accept it and design asynchronously.",
    sections: [
      { h: "Reading the availability grid", body: "The grid walks all twenty-four UTC hours and prints the local clock time for each participant, marking rows where everyone falls inside their own working day. Any green row is a defensible meeting slot; a blank grid means someone must take a call outside their hours, and that cost should rotate rather than always land on the most junior person." },
      { h: "Designing around a thin overlap", body: "When overlap drops below two hours, use it exclusively for decisions and unblocking. Push status, demos and design reviews into recorded video and written documents with a twenty-four hour response expectation. The handover note at the end of each region's day becomes the highest-leverage artefact the team produces." },
      { h: "Daylight saving breaks your calendar twice a year", body: "The US, EU and UK shift on different dates, and much of Asia, Africa and South America does not shift at all. For two to three weeks each spring and autumn, a fixed UTC meeting drifts by an hour for some participants. Anchor recurring invites to a specific city time zone rather than to UTC so the calendar handles the shift for you." },
    ],
    staticTable: {
      title: "Working-hour overlap between major hubs (9-17 local)",
      head: ["Pair", "Offset gap", "Daily overlap", "Best slot (UTC)"],
      rows: [
        ["London ↔ New York", "5 h", "3 h", "14:00-16:00"],
        ["London ↔ Bengaluru", "4.5 h", "3.5 h", "09:00-11:30"],
        ["New York ↔ Bengaluru", "9.5 h", "0 h", "None — async"],
        ["Berlin ↔ Dubai", "2 h", "6 h", "08:00-13:00"],
        ["San Francisco ↔ London", "8 h", "1 h", "16:00-17:00"],
        ["Sydney ↔ San Francisco", "18 h", "0 h", "None — async"],
      ],
    },
    faqs: [
      { q: "What is the best meeting time between London and New York?", a: "Between 14:00 and 16:00 UTC, which is 14:00-16:00 in London and 09:00-11:00 in New York — inside standard working hours for both." },
      { q: "How do I schedule across three time zones with no overlap?", a: "Rotate the inconvenient slot so the same team never absorbs the early or late call, and shift everything except live decisions to written asynchronous handovers." },
      { q: "Should recurring invites be anchored to UTC?", a: "No. Anchor them to a specific city so daylight saving changes are applied automatically. UTC-anchored invites silently drift for half the participants twice a year." },
    ],
    related: ["business-days-calculator", "sleep-cycle-rem-calculator", "reading-time-calculator", "chronological-date-gap-calculator"],
  },

  {
    slug: "business-days-calculator",
    cluster: "datetime",
    name: "Business Working Days",
    h1: "Business Days Calculator (Exclude Weekends and Custom Holidays)",
    title: "Business Days Calculator — Exclude Weekends & Holidays",
    description:
      "Count working days between two dates, excluding weekends and any number of custom public holidays, with a weekend-day and calendar-day breakdown.",
    keywords: [
      "business days calculator exclude weekends custom holidays",
      "how many working days between two dates",
      "net working days formula excluding public holidays",
      "delivery date calculator business days only",
    ],
    inputs: [
      { id: "start", label: "Start date", type: "date", def: "2026-08-06" },
      { id: "end", label: "End date", type: "date", def: "2026-12-31" },
      { id: "weekend", label: "Weekend pattern", type: "select", def: "satsun", options: [
        { value: "satsun", label: "Saturday + Sunday" },
        { value: "frisat", label: "Friday + Saturday (Gulf)" },
        { value: "sun", label: "Sunday only" },
        { value: "none", label: "No weekend — 7 day operation" },
      ] },
      { id: "holidays", label: "Public holidays in range", type: "number", def: "4", min: 0, max: 60 },
    ],
    compute: (v) => {
      const a = new Date(s(v, "start")), b = new Date(s(v, "end"));
      const valid = !isNaN(a.getTime()) && !isNaN(b.getTime()) && b >= a;
      const pattern = s(v, "weekend", "satsun");
      const isWeekend = (d: number) =>
        pattern === "satsun" ? d === 0 || d === 6 : pattern === "frisat" ? d === 5 || d === 6 : pattern === "sun" ? d === 0 : false;
      let total = 0, weekends = 0;
      if (valid) {
        const cur = new Date(a);
        while (cur <= b) {
          total++;
          if (isWeekend(cur.getDay())) weekends++;
          cur.setDate(cur.getDate() + 1);
        }
      }
      const holidays = n(v, "holidays");
      const working = Math.max(0, total - weekends - holidays);
      return {
        cards: [
          { label: "Working days", value: fmt(working), sub: `${fmt(working * 8)} hours at 8 h/day`, accent: true },
          { label: "Calendar days", value: fmt(total), sub: `${fmt(total / 7, 1)} weeks` },
          { label: "Weekend days", value: fmt(weekends), sub: pattern === "frisat" ? "Friday + Saturday" : pattern === "sun" ? "Sunday only" : pattern === "none" ? "None" : "Saturday + Sunday" },
          { label: "Holidays excluded", value: fmt(holidays), sub: "As entered" },
        ],
        table: {
          title: "Breakdown",
          head: ["Category", "Days", "Share of range"],
          rows: [
            ["Working days", fmt(working), `${fmt((working / Math.max(total, 1)) * 100, 1)}%`],
            ["Weekend days", fmt(weekends), `${fmt((weekends / Math.max(total, 1)) * 100, 1)}%`],
            ["Public holidays", fmt(holidays), `${fmt((holidays / Math.max(total, 1)) * 100, 1)}%`],
            ["Total calendar days", fmt(total), "100%"],
          ],
        },
        summary: valid ? `${fmt(working)} billable days at an eight-hour standard equals ${fmt(working * 8)} working hours.` : "End date must be on or after the start date.",
      };
    },
    intro:
      "Contracts, SLAs, notice periods and shipping estimates are almost always written in business days, and everyone counts them slightly differently. This calculator makes the two ambiguous parts explicit: which days count as the weekend, and how many public holidays fall inside your specific range.",
    sections: [
      { h: "Weekend patterns are not universal", body: "Saturday and Sunday is standard across the Americas, Europe and most of Asia. Several Gulf states run Friday and Saturday, and some countries — including India for many sectors — treat only Sunday as the weekend. Choosing the wrong pattern on a six-month project can shift the count by more than twenty days." },
      { h: "Inclusive versus exclusive counting", body: "Legal notice periods usually exclude the start date and include the end date, while shipping estimates typically count from the next business day. This tool counts both endpoints inclusively. If your contract excludes the first day, subtract one working day from the result." },
      { h: "Planning around holiday clusters", body: "Late December, Easter week, Golden Week in Japan and Eid periods across Muslim-majority countries all cluster holidays together and effectively remove a working week. When a delivery window crosses one of those clusters, add the holidays explicitly rather than assuming an average monthly figure." },
    ],
    staticTable: {
      title: "Working days per month in a typical year",
      head: ["Month", "Calendar days", "Weekend days", "Typical working days"],
      rows: [
        ["January", 31, 8, 21],
        ["February", 28, 8, 20],
        ["March", 31, 9, 22],
        ["April", 30, 8, 21],
        ["June", 30, 9, 21],
        ["August", 31, 10, 21],
        ["November", 30, 9, 20],
        ["December", 31, 8, 20],
      ],
      caption: "Before national public holidays are deducted.",
    },
    faqs: [
      { q: "How many working days are in a year?", a: "About 261 before holidays — 365 calendar days minus 104 weekend days. Deducting eight to twelve public holidays leaves roughly 249-253 in most Tier-1 countries." },
      { q: "Do business days include public holidays?", a: "No. A business day is a day the relevant office is normally open, so both weekends and public holidays are excluded from the count." },
      { q: "How do I count business days for a delivery estimate?", a: "Most carriers start counting from the next business day after dispatch, not the dispatch day itself. Subtract one from an inclusive count to match that convention." },
    ],
    related: ["chronological-date-gap-calculator", "timezone-meeting-overlap-finder", "milestone-birthday-countdown", "hourly-to-annual-salary-converter"],
  },

  {
    slug: "day-of-week-born-calculator",
    cluster: "datetime",
    name: "Day of the Week You Were Born",
    h1: "Day of the Week You Were Born (Calendar Facts Lookup)",
    title: "What Day of the Week Was I Born? Calendar Lookup",
    description:
      "Find the weekday of any birth date, when your birthday next repeats on that weekday, how many times it has fallen on each day, and your birth-date facts.",
    keywords: [
      "day of the week you were born calendar facts lookup",
      "what day was i born on calculator",
      "when will my birthday fall on a saturday again",
      "zeller congruence day of week formula",
    ],
    inputs: [
      { id: "dob", label: "Date of birth", type: "date", def: "1992-11-07" },
    ],
    compute: (v) => {
      const d = new Date(s(v, "dob"));
      const valid = !isNaN(d.getTime());
      const wd = valid ? WD[d.getDay()] : "—";
      const now = new Date();
      const counts: Record<string, number> = {};
      for (let y = d.getFullYear(); y <= now.getFullYear(); y++) {
        const b = new Date(y, d.getMonth(), d.getDate());
        if (b <= now) counts[WD[b.getDay()]] = (counts[WD[b.getDay()]] ?? 0) + 1;
      }
      const future: (string | number)[][] = [];
      for (let y = now.getFullYear(); future.length < 8; y++) {
        const b = new Date(y, d.getMonth(), d.getDate());
        if (b < now) continue;
        const age = y - d.getFullYear();
        future.push([y, age, WD[b.getDay()], [0, 6].includes(b.getDay()) ? "Weekend 🎉" : "Weekday"]);
      }
      const nextSameDay = future.find((f) => f[2] === wd);
      const dayOfYear = valid ? Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000) : 0;
      return {
        cards: [
          { label: "You were born on a", value: wd, sub: valid ? d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "", accent: true },
          { label: "Next birthday on that day", value: nextSameDay ? String(nextSameDay[0]) : "Beyond 8 years", sub: nextSameDay ? `You will turn ${nextSameDay[1]}` : "" },
          { label: "Day of the year", value: `${dayOfYear} of 365`, sub: `Week ${Math.ceil(dayOfYear / 7)}` },
          { label: "Weekend birthdays so far", value: fmt((counts["Saturday"] ?? 0) + (counts["Sunday"] ?? 0)), sub: "Saturdays and Sundays combined" },
        ],
        table: { title: "Your next eight birthdays", head: ["Year", "Age", "Weekday", "Party friendly?"], rows: future },
        summary: "Birthdays advance one weekday each year and two across a leap day, so the same weekday returns on a 5-6-11-6 year pattern.",
      };
    },
    intro:
      "Every date in the Gregorian calendar has a fixed weekday determined by simple modular arithmetic — Zeller's congruence or the doomsday algorithm both produce it in a few operations. Because 365 leaves a remainder of one when divided by seven, your birthday shifts forward one weekday each year and two whenever a leap day intervenes.",
    sections: [
      { h: "The 28-year calendar cycle", body: "The complete pattern of weekdays and leap years repeats every twenty-eight years, so the calendar of 1998 is identical to 2026. That means your birthday falls on the same weekday every twenty-eight years, with intermediate repeats at irregular five, six and eleven year gaps depending on where leap years land." },
      { h: "How Zeller's congruence works", body: "Zeller's formula treats January and February as months thirteen and fourteen of the previous year, then combines the day, a month term, the year within the century and the century itself modulo seven. It was published in 1882 and is still what most date libraries reduce to under the hood." },
      { h: "Why weekend birthdays feel rare", body: "Two of seven weekdays are weekend days, so roughly twenty-nine percent of birthdays land on one — about two in every seven years. The perception of scarcity comes from clustering: because of the one-and-two shift pattern, weekend birthdays often arrive in pairs and then vanish for four or five years." },
    ],
    staticTable: {
      title: "Weekday shift pattern for a fixed date",
      head: ["Year offset", "Weekday shift", "Cause"],
      rows: [
        ["+1 (common year)", "+1 day", "365 mod 7 = 1"],
        ["+1 (across leap day)", "+2 days", "366 mod 7 = 2"],
        ["+6 years", "Often same weekday", "Depends on leap placement"],
        ["+11 years", "Often same weekday", "Depends on leap placement"],
        ["+28 years", "Always same weekday", "Full calendar cycle"],
      ],
    },
    faqs: [
      { q: "What day of the week was I born on?", a: "Enter your date of birth above and the calculator resolves the exact weekday, along with the day-of-year number and how often your birthday has landed on a weekend." },
      { q: "When will my birthday fall on a Saturday again?", a: "Usually within five to eleven years, depending on leap-year placement. The eight-year table above lists each upcoming weekday directly." },
      { q: "Does the calendar really repeat every 28 years?", a: "Yes within the same century, because the leap-year cycle of four and the seven-day week produce a common period of twenty-eight. Century years not divisible by four hundred break the pattern." },
    ],
    related: ["milestone-birthday-countdown", "chronological-date-gap-calculator", "business-days-calculator", "half-plus-seven-age-gap-calculator"],
  },

  {
    slug: "ivf-due-date-calculator",
    cluster: "datetime",
    name: "IVF Pregnancy Due Date",
    h1: "IVF Pregnancy Due Date Calculator (3-Day and 5-Day Embryo Transfer)",
    title: "IVF Due Date Calculator — 3-Day & 5-Day Embryo Transfer",
    description:
      "Calculate an IVF due date from the embryo transfer date and embryo stage, with gestational age today, trimester dates and key scan milestones.",
    keywords: [
      "ivf pregnancy due date calculator 3day 5day embryo",
      "frozen embryo transfer due date calculation",
      "gestational age after blastocyst transfer",
      "ivf due date vs lmp due date difference",
    ],
    inputs: [
      { id: "transfer", label: "Embryo transfer date", type: "date", def: "2026-05-01" },
      { id: "stage", label: "Embryo stage at transfer", type: "select", def: "5", options: [
        { value: "3", label: "Day 3 — cleavage stage" },
        { value: "5", label: "Day 5 — blastocyst" },
        { value: "6", label: "Day 6 — expanded blastocyst" },
      ] },
    ],
    compute: (v) => {
      const t = new Date(s(v, "transfer"));
      const stage = n(v, "stage", 5);
      const valid = !isNaN(t.getTime());
      // Conception equivalent = transfer date minus embryo age; due date = conception + 266 days.
      const conception = new Date(t); conception.setDate(t.getDate() - stage);
      const due = new Date(conception); due.setDate(conception.getDate() + 266);
      const lmp = new Date(due); lmp.setDate(due.getDate() - 280);
      const now = new Date();
      const gestDays = valid ? Math.floor((now.getTime() - lmp.getTime()) / 86400000) : 0;
      const w = Math.floor(gestDays / 7), d = gestDays % 7;
      const at = (days: number) => { const x = new Date(lmp); x.setDate(lmp.getDate() + days); return x.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); };
      return {
        cards: [
          { label: "Estimated due date", value: valid ? due.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—", sub: `Day ${stage} transfer basis`, accent: true },
          { label: "Gestational age today", value: gestDays > 0 ? `${w}w ${d}d` : "Before transfer", sub: gestDays > 0 ? `${fmt(Math.max(0, 280 - gestDays))} days to go` : "" },
          { label: "Equivalent LMP date", value: valid ? lmp.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—", sub: "What charts will show" },
          { label: "Trimester", value: gestDays < 91 ? "First" : gestDays < 189 ? "Second" : "Third", sub: `${fmt(Math.min(100, (gestDays / 280) * 100))}% complete` },
        ],
        table: {
          title: "Milestone dates for this cycle",
          head: ["Milestone", "Gestational age", "Date"],
          rows: [
            ["First beta hCG", "4w 0d", at(28)],
            ["Viability scan", "6w 3d", at(45)],
            ["End of first trimester", "13w 0d", at(91)],
            ["NT / combined screening", "12w 0d", at(84)],
            ["Anatomy scan", "20w 0d", at(140)],
            ["Viability threshold", "24w 0d", at(168)],
            ["Third trimester begins", "27w 0d", at(189)],
            ["Full term", "39w 0d", at(273)],
            ["Due date", "40w 0d", at(280)],
          ],
        },
        summary: "IVF due dates are more precise than LMP-based ones because the exact conception equivalent is known to the day.",
      };
    },
    intro:
      "IVF removes the biggest source of error in pregnancy dating. With a natural conception the due date is estimated from the last menstrual period and assumes a textbook fourteen-day ovulation that many cycles do not follow. After an embryo transfer the developmental age of the embryo is known exactly, so the due date can be fixed to the day.",
    sections: [
      { h: "How the transfer date converts to gestational age", body: "Gestational age is counted from the notional last menstrual period, two weeks before conception. A day-5 blastocyst transferred today is already nineteen days of gestational age — fourteen days of the notional pre-ovulatory phase plus five days of embryo development. Subtract the embryo age from the transfer date to get the conception equivalent, then add 266 days." },
      { h: "Day 3 versus day 5 transfers", body: "A cleavage-stage day-3 embryo is transferred two days earlier in its development than a blastocyst, so its due date sits two days later from the same transfer date. Day-6 expanded blastocysts shift one day earlier again. Getting the stage right matters most for early scan scheduling, where a two-day error can change the interpretation of a viability scan." },
      { h: "Frozen transfers use the same maths", body: "For a frozen embryo transfer, only the embryo's developmental stage at freezing and the transfer date matter — the time spent in cryostorage does not count toward gestational age at all. A blastocyst frozen for three years is still a day-5 embryo on the day it is transferred." },
    ],
    staticTable: {
      title: "Due date offset by embryo stage",
      head: ["Embryo stage", "Days added to transfer date", "Gestational age on transfer day", "Notes"],
      rows: [
        ["Day 2 (4-cell)", "266 − 2 = 264", "2w 2d", "Uncommon in modern protocols"],
        ["Day 3 (cleavage)", "266 − 3 = 263", "2w 3d", "Standard cleavage transfer"],
        ["Day 5 (blastocyst)", "266 − 5 = 261", "2w 5d", "Most common today"],
        ["Day 6 (expanded)", "266 − 6 = 260", "2w 6d", "Frequent with FET cycles"],
        ["Day 7 blastocyst", "266 − 7 = 259", "3w 0d", "Slower-developing embryos"],
      ],
    },
    faqs: [
      { q: "How is an IVF due date calculated for a 5-day transfer?", a: "Add 261 days to the transfer date — equivalent to subtracting the five days of embryo development from the transfer date and then adding the standard 266 days from conception to birth." },
      { q: "Why does my IVF due date differ from the LMP calculator?", a: "LMP dating assumes a 28-day cycle with ovulation on day 14. IVF dating uses the known embryo age, which is exact, so the two can differ by several days in either direction." },
      { q: "Does time in the freezer affect the due date?", a: "No. Cryopreservation pauses development entirely, so only the embryo's stage at freezing and the transfer date are used." },
    ],
    related: ["chronological-date-gap-calculator", "milestone-birthday-countdown", "business-days-calculator", "tdee-calculator-weight-loss"],
  },
];